import { storyStep, StepMap2Enum, MemberStepInStory } from '../../utils/constant'
import { wordSimplified } from '../../utils/helperFunction'
import {
  SET_CURRENT_STEP,
  SET_STORIES,
  SET_STORY,
  SET_LAST_STEP,
  SET_PREVENT_REDIRECT,
  SET_SHOW_RECORDING_SUCCESS,
  SET_STORY_LOADING,
  SET_STORIES_LIST,
  SET_STORIES_LIST_LOADER,
  SET_IS_SUBMIT,
  SET_PINYIN_SETTINGS,
  SET_WORD_DETAIL,
  SET_WORD_TYPES_OF_CURRENT_STORY,
  PATCH_STORY_WORD_TYPE,
  CHECK_WORD_IS_CLICKED,
  SET_STORY_DIFFICULTY,
  SET_AUDIO_PRONUNCIATION_SETTING,
  SET_STORY_DICTIONARY,
  PATCH_STORY_DICTIONARY_WORD,
  CLEAR_STORY_DICTIONARY,
  SET_DICTIONARY_LOADING,
  RESET_STORY,
  SET_PREFERRED_STORY_LEVEL,
  SET_STORIES_BY_LEVEL,
  SET_STORIES_BY_LEVEL_LOADING,
} from '../types'

interface IStoryState {
  stories: any
  storyLoading: boolean
  currentStory: any
  currentStep: any
  lastStep: any
  lastStepEnum: any
  preventRedirect: boolean
  showRecordingSuccess: boolean
  storiesList: any
  storiesListLoader: boolean
  user_exist: boolean
  storyLevel: string
  welcomeMessage: string
  /** The key the web translates the greeting by (null for a campaign message). */
  welcomeMessageKey: string | null
  story_submitted: any
  CTAMessage: any
  pinyin_settings: any
  wordDetailLoader: boolean
  wordDetails: any
  isWordClick: boolean
  difficulty_level: number
  audio_pronunciation_setting: 'both' | 'female_only' | 'male_only' | 'none'
  storyDictionary: { [word: string]: any } | null
  dictionaryLoading: boolean
  preferredStoryLevel: string | null
  storiesByLevel: any[]
  storiesByLevelLoading: boolean
}

const initialState: IStoryState = {
  stories: [],
  storyLoading: true,
  currentStory: {},
  currentStep: storyStep.Intro,
  lastStep: null,
  lastStepEnum: null,
  preventRedirect: false,
  showRecordingSuccess: false,
  storiesList: [],
  storiesListLoader: true,
  user_exist: false,
  storyLevel: '',
  welcomeMessage: '',
  welcomeMessageKey: null,
  story_submitted: null,
  CTAMessage: null,
  pinyin_settings: { new_word: 'always', recently_learned_word: 'on_hover', known_word: 'never' },
  difficulty_level: 1,
  audio_pronunciation_setting: 'both',
  wordDetailLoader: false,
  wordDetails: null,
  isWordClick: false,
  storyDictionary: null,
  dictionaryLoading: false,
  preferredStoryLevel: null,
  storiesByLevel: [],
  storiesByLevelLoading: false,
}

export default function storyReducer(state = initialState, action: any) {
  switch (action.type) {
    case RESET_STORY:
      return {
        ...initialState,
      }

    case SET_STORY_LOADING:
      const tempPayload = action?.payload || {}
      return {
        ...state,
        ...tempPayload,
      }
    case SET_STORIES:
      return {
        ...state,
        stories: [...action.payload],
        currentStory: {},
      }
    case SET_STORY:
      return {
        ...state,
        currentStory: {
          ...action.payload,
        },
        // The failed-load path in getStory clears the story with a null payload.
        storyLevel: action.payload?.level,
      }
    case SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
      }
    case SET_LAST_STEP:
      const lastStep = action.payload || MemberStepInStory.INTRODUCTION
      return {
        ...state,
        lastStep: lastStep,
        lastStepEnum: StepMap2Enum[lastStep],
      }
    case SET_PREVENT_REDIRECT:
      return {
        ...state,
        preventRedirect: action.payload,
      }
    case SET_SHOW_RECORDING_SUCCESS:
      return {
        ...state,
        showRecordingSuccess: action.payload,
      }
    case SET_STORIES_LIST:
      return {
        ...state,
        storiesList: JSON.stringify(action.payload?.data) === '{}' ? [] : action.payload?.data,
        user_exist: action.payload?.user_exist,
        welcomeMessage: action.payload?.welcomeMessage,
        welcomeMessageKey: action.payload?.welcomeMessageKey ?? null,
        CTAMessage: action.payload?.CTAMessage,
      }
    case SET_STORIES_LIST_LOADER:
      return {
        ...state,
        storiesListLoader: action.payload,
      }
    case SET_IS_SUBMIT:
      return {
        ...state,
        story_submitted: action.payload,
      }
    case SET_PINYIN_SETTINGS:
      return {
        ...state,
        pinyin_settings: action.payload,
      }
    case SET_STORY_DIFFICULTY:
      return {
        ...state,
        difficulty_level: action.payload,
      }
    case SET_AUDIO_PRONUNCIATION_SETTING:
      return {
        ...state,
        audio_pronunciation_setting: action.payload,
      }
    case SET_WORD_DETAIL:
      return {
        ...state,
        wordDetailLoader: action.payload.loader,
        wordDetails: action.payload.detail,
      }
    case SET_WORD_TYPES_OF_CURRENT_STORY:
      return {
        ...state,
        currentStory: {
          ...state?.currentStory,
          ...action.payload,
        },
      }
    // Move ONLY the interacted word between mastery buckets, leaving every other
    // word's highlight untouched. Replacing the whole arrays here used to repaint
    // every underline from a single tap — if the displayed arrays were stale, all
    // highlights collapsed at once (see word-underline disappearance bug).
    case PATCH_STORY_WORD_TYPE: {
      const { word, mastery } = action.payload || {}
      const currentStory = state?.currentStory
      if (!currentStory || !word || !mastery) return state

      const masteryToBucket = {
        New: 'newWordsArray',
        Learning: 'learningWords',
        Growing: 'growingWords',
        Developing: 'developingWords',
        Mastered: 'masteredWords',
      }
      const targetKey = masteryToBucket[mastery]
      if (!targetKey) return state

      const bucketKeys = Object.values(masteryToBucket)
      const normalized = wordSimplified(word)

      // Preserve the existing word object (pinyin/english) if the word is already tracked.
      let wordObj = { word }
      for (const key of bucketKeys) {
        const found = (currentStory[key] || []).find((w) => wordSimplified(w?.word) === normalized)
        if (found) {
          wordObj = found
          break
        }
      }

      const nextStory = { ...currentStory }
      for (const key of bucketKeys) {
        if (Array.isArray(currentStory[key])) {
          nextStory[key] = currentStory[key].filter((w) => wordSimplified(w?.word) !== normalized)
        }
      }
      nextStory[targetKey] = [...(nextStory[targetKey] || []), wordObj]

      return { ...state, currentStory: nextStory }
    }
    case CHECK_WORD_IS_CLICKED:
      return {
        ...state,
        isWordClick: action.payload,
      }
    case SET_STORY_DICTIONARY:
      return {
        ...state,
        storyDictionary: action.payload,
        dictionaryLoading: false,
      }
    case PATCH_STORY_DICTIONARY_WORD: {
      const { word, patch } = action.payload || {}
      if (!word || !state.storyDictionary || !state.storyDictionary[word]) return state
      return {
        ...state,
        storyDictionary: {
          ...state.storyDictionary,
          [word]: { ...state.storyDictionary[word], ...patch },
        },
      }
    }
    case CLEAR_STORY_DICTIONARY:
      return {
        ...state,
        storyDictionary: null,
        dictionaryLoading: false,
      }
    case SET_DICTIONARY_LOADING:
      return {
        ...state,
        dictionaryLoading: action.payload,
      }
    case SET_PREFERRED_STORY_LEVEL:
      return {
        ...state,
        preferredStoryLevel: action.payload,
      }
    case SET_STORIES_BY_LEVEL:
      return {
        ...state,
        storiesByLevel: action.payload,
      }
    case SET_STORIES_BY_LEVEL_LOADING:
      return {
        ...state,
        storiesByLevelLoading: action.payload,
      }
    default:
      return state
  }
}
